import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* eslint-disable import/no-cycle */
import React, { useState, useEffect, useCallback, useContext } from "react";
import { apiApplyPromo, apiGetPricingSummary, apiRemovePromo } from "Root/api/apiCheckout";
import { Loading, ProductCard, HeadingBlock, NavBar } from "CheckoutComponents";
import { CheckoutContext } from "Root/checkout/CheckoutRoutes";
import { useCartHandlers, useFlavors } from "CheckoutComponents/Checkout/hooks";
import waterbottle from "Images/checkout/waterbottle.png";
import blenderbottle from "Images/checkout/blenderbottle.png";
import getAdditionalProducts from "CheckoutComponents/Checkout/helpers/getAdditionalProducts";
import { sendPageViewToGA } from "Root/checkout/utils/ga4";
import styles from "./styles.module.css";
import posthog from "posthog-js";
import UnderlinedButton from "Root/profile/components/common/UnderlinedButton";
import { useDispatch, useSelector } from "react-redux";
import { fetchIngredients, setBlendChanged, updateUserCart } from "Root/checkout/redux/slices/checkoutUserCartSlice";
import { useRetakeQuizData } from "Root/profile/components/RetakeQuizDrawer/useRetakeQuizData";
import RetakeQuizDrawer from "Root/profile/components/RetakeQuizDrawer";
function CartPage() {
    const [addonAttributes, setAddonAttributes] = useState(null);
    const dispatch = useDispatch();
    const { cart, setCart, userCart, setUserCart, updatingUserCart, addItemToCart, removeItemFromCart, error_message, checkoutExperience, bannerData, fetchUserCart, checkoutPages, } = useContext(CheckoutContext);
    const ingredients = useSelector((state) => state.checkoutUserCart.ingredients);
    const blendChanged = useSelector((state) => state.checkoutUserCart.blendChanged);
    const { personalizedProducts, performanceBoosts, accessories, loading } = useFlavors({
        error_message,
        addonAttributes,
        setAddonAttributes,
    });
    const { addToCart, removeFromCart } = useCartHandlers({
        cart,
        setCart,
        updatingUserCart,
        addItemToCart,
        removeItemFromCart,
    });
    const data = useRetakeQuizData();
    const onClickRetake = () => {
        data.toggleDrawer(true)();
    };
    const additionalProducts = getAdditionalProducts({ cart, personalizedProducts, performanceBoosts, accessories });
    useEffect(() => {
        window.scrollTo(0, 0);
        sendPageViewToGA("new-pdp");
        posthog?.capture("$pageview");
    }, []);
    const [discountStr, setDiscountStr] = useState([]);
    const [deliveryFrequency, setDeliveryFrequency] = useState("");
    const deliveryString = `Delivered every ${deliveryFrequency}`;
    useEffect(() => {
        const getPricingSummary = async () => {
            const data = await apiGetPricingSummary("standard");
            if (data) {
                setDiscountStr(data.discount_str);
                setDeliveryFrequency(data.delivery_frequency);
            }
        };
        getPricingSummary();
        dispatch(fetchIngredients(true));
        if (blendChanged) {
            fetchUserCart();
            dispatch(setBlendChanged(false));
        }
    }, []);
    const applyPromo = useCallback(async (promo) => {
        const response = await apiApplyPromo({ promo, shippingSpeed: "standard" });
        setUserCart(response.userCart);
        dispatch(updateUserCart(response.userCart));
        setDiscountStr(response.discount_str);
        return response;
    }, [setUserCart]);
    const removePromo = useCallback(async (promo) => {
        const response = await apiRemovePromo({ promo, shippingSpeed: "standard" });
        setUserCart(response.userCart);
        dispatch(updateUserCart(response.userCart));
        setDiscountStr(response.discount_str);
        return response;
    }, [setUserCart]);
    const simplifyTitle = (title) => title.replace("Personalized", "").replace("Performance Boosts", "").replace("Performance Boost", "").trim();
    const goToQuiz = () => {
        window.location.href = "/quiz-new/quiz/";
    };
    const submitAnswers = async () => {
        await data.updateQuizData();
        fetchUserCart();
    };
    const getIngredients = (item_type) => {
        return ingredients.find((ingredient) => ingredient.product_key === item_type);
    };
    const fullwidth_products = userCart.map((item) => {
        const isGreens = item.title.toLowerCase().includes("greens");
        return (_jsx(ProductCard, { image: item.item_type === "blenderbottle" ? blenderbottle : item.checkout_image_url, isRow: true, servings: item.servings && (isGreens ? `${item.servings} on-the-go packs` : `${item.servings} servings`), regularPrice: item.gross_price, netPrice: item.net_price, title: simplifyTitle(item.title), type: item.item_type, removeFromCart: removeFromCart, category: item.item_category, label: ["blenderbottle", "waterbottle", "other", "wb221", "wb222"].includes(item.item_type) ? "Delivered Once" : "", cartItem: item, ingredientInfo: getIngredients(item.item_type === "boost" ? item.item_key : item.item_type), description: "" }, item.title));
    });
    const carousel_products = additionalProducts.map((item) => {
        const bottleImage = item.item_key === "waterbottle" ? waterbottle : blenderbottle;
        const item_to_add = item.item_type === "boost" ? item.item_key : item.item_type;
        return (_jsx(ProductCard, { image: item.image_url || bottleImage, isCentered: true, regularPrice: item.gross_price, netPrice: item.net_price, title: simplifyTitle(item.title), type: item.item_type, description: item.product_explanation, addToCart: () => addToCart(item_to_add), category: item.item_category, label: item.item_type === "blenderbottle" ? "Recommended" : "", cartItem: {
                ...item,
                item_type: item.item_type,
                description: item.product_explanation,
                servings: item.servings?.replace("servings", ""),
                attributes: [],
                available_flavors: [],
                bags_info: [],
                flavors: [],
                item_category: item.item_category,
                short_name: item.item_key,
                title: item.title,
                item_key: item.item_key,
                max_flavors: 2,
                product_id: item.item_key,
                quantity_description: "",
                sku: "",
                container_type: "",
                checkout_image_url: "",
            }, ingredientInfo: getIngredients(item.item_type === "boost" ? item.item_key : item.item_type) }, item.title));
    });
    return (_jsxs("div", { className: "checkout-container", children: [_jsx(NavBar, { active: 2, cart: cart, bannerData: bannerData }), _jsxs("div", { className: styles.container, children: [_jsx("div", { className: styles.system, children: _jsx("div", { className: styles.desc, children: _jsx(HeadingBlock, { isMainTitle: false, title: "Your Gainful System", children: "Add or subtract any of the below recommended Gainful products. Tailor your products to meet your exact needs." }) }) }), _jsx("div", { className: styles.productarea, children: fullwidth_products.length > 0 ? (_jsxs(_Fragment, { children: [fullwidth_products, _jsx("div", { className: "text-center mb-6 text-sm", children: "All discounts to be applied at checkout" }), _jsxs("div", { className: styles.btn, children: [_jsx(UnderlinedButton, { label: "Update Quiz Answers", onClick: onClickRetake }), _jsx(RetakeQuizDrawer, { ...data, submitAnswers: submitAnswers, asDrawer: true })] })] })) : (_jsxs(_Fragment, { children: [_jsx(Loading, {}), _jsx(Loading, {}), _jsx(Loading, {})] })) }), !loading ? (_jsx(_Fragment, { children: carousel_products?.length > 0 &&
                            ((checkoutExperience !== "influencer" && userCart?.length < 9) ||
                                checkoutExperience === "influencer") && (_jsxs("div", { className: styles.upsellarea, children: [_jsx("div", { className: styles.subtitle, children: "You may also like" }), _jsx("div", { className: styles.cardswrap, children: _jsx("div", { className: styles.cards, children: carousel_products }) })] })) })) : (_jsxs("div", { className: styles.squareloadingcontainer, children: [_jsx("div", { className: styles.squareloading33, children: _jsx(Loading, { isSquare: true }) }), _jsx("div", { className: styles.squareloading33, children: _jsx(Loading, { isSquare: true }) }), _jsx("div", { className: styles.squareloading33, children: _jsx(Loading, { isSquare: true }) })] }))] })] }));
}
export default CartPage;
